import { ENVS } from "~/utils";
import {
  clearAffiliateRefFromStorage,
  consumeAffiliateVisitPending,
  readAffiliateRefFromStorage,
} from "~/utils/auth/affiliateRefStorage";

const LAMBDA_API_URL = ENVS.lambda_api_url;

export interface EnsureUserParams {
  id: string;
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  /** Optional override; otherwise read from localStorage (?ref= capture). */
  affiliateCode?: string;
}

export interface EnsureUserOptions {
  /** Required: DynamoDB + Firestore ensure via Lambda POST /action/sign-in */
  idToken: string;
}

/**
 * Ensures a user exists in DynamoDB and Firestore via Lambda sign-in action.
 * Firestore profile create/check runs server-side (Admin SDK).
 * Passes stored affiliate referral code for first-touch attribution on new users.
 * Optionally asks the backend to record a visit in the same call (signup?ref=).
 */
export const ensureUserExists = async (
  params: EnsureUserParams,
  options: EnsureUserOptions
): Promise<{ success: boolean; error?: unknown }> => {
  const { id, email, username, firstName, lastName } = params;
  const affiliateCode =
    (typeof params.affiliateCode === "string" && params.affiliateCode.trim()) ||
    readAffiliateRefFromStorage();
  const recordAffiliateVisit = consumeAffiliateVisitPending();

  try {
    if (!options.idToken) {
      return {
        success: false,
        error: "idToken is required for sign-in",
      };
    }

    if (!LAMBDA_API_URL) {
      return {
        success: false,
        error: "LAMBDA_API_URL is not configured",
      };
    }

    const res = await fetch(`${LAMBDA_API_URL}/action/sign-in`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${options.idToken}`,
      },
      body: JSON.stringify({
        id,
        email,
        username: username || email,
        firstName: firstName || "",
        lastName: lastName || "",
        ...(affiliateCode ? { affiliateCode } : {}),
        ...(recordAffiliateVisit && affiliateCode
          ? { recordAffiliateVisit: true }
          : {}),
      }),
    });

    const data = await res.json();
    if (!res.ok) {
      console.error("Lambda sign-in action failed", data);
      return {
        success: false,
        error: data?.error ?? "Sign-in action failed",
      };
    }
    if (!data?.success) {
      return { success: false, error: data?.error };
    }

    if (affiliateCode) {
      clearAffiliateRefFromStorage();
    }

    return { success: true };
  } catch (error) {
    console.error("Error ensuring user exists", error);
    return {
      success: false,
      error,
    };
  }
};
